import { useState, useEffect, useCallback } from "react";
import HelpButton from "../components/HelpButton";
import axios from "axios";
import api from "../lib/api";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import { Plus, X, PencilSimple, Trash, Buildings, ArrowsClockwise } from "@phosphor-icons/react";

const empty = {
  name: "", code: "", parent_department_id: "",
  department_head_id: "", department_head_name: "",
  description: "", status: "active",
};

const AdminDepartments = ({ user, logout }) => {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, u] = await Promise.all([
        axios.get(`${API}/departments`),
        api.get(`/users`),
      ]);
      setItems(d.data || []);
      setUsers(u.data || []);
    } catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/departments/${editing.department_id}?requester_role=${user?.role || ""}`, form);
        toast.success("Department updated");
      } else {
        await axios.post(`${API}/departments?requester_role=${user?.role || ""}`, form);
        toast.success("Department created");
      }
      setShowForm(false); setEditing(null); setForm(empty);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const startEdit = (d) => { setEditing(d); setForm({ ...empty, ...d }); setShowForm(true); };

  const remove = async (d) => {
    if (!window.confirm(`Delete department "${d.name}"?`)) return;
    try {
      await api.delete(`/departments/${d.department_id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const seedDefaults = async () => {
    try {
      const res = await axios.post(`${API}/org/seed-defaults?requester_role=${user?.role || ""}`);
      toast.success(`Seeded ${res.data.created_departments} departments, ${res.data.created_roles} roles`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const inputCls = "rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-2.5 w-full outline-none text-sm";
  const labelCls = "text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1";

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64">
        <div className="p-8">
          <div className="flex justify-between items-start mb-8 flex-wrap gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-1">Org Structure</div>
              <div className="flex items-center"><h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>
                Departments
              </h1><HelpButton moduleKey="admin_settings" /></div>
              <p className="text-base leading-relaxed text-slate-700">{items.length} departments configured</p>
            </div>
            <div className="flex gap-2">
              <button data-testid="dept-seed-btn" onClick={seedDefaults} className="rounded-none border border-slate-300 hover:bg-slate-50 text-slate-700 px-4 py-2 text-sm font-medium inline-flex items-center gap-2">
                <ArrowsClockwise size={14} /> Seed Defaults
              </button>
              <button
                data-testid="dept-add-btn"
                onClick={() => { setEditing(null); setForm(empty); setShowForm(true); }}
                className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2.5 font-medium transition-colors inline-flex items-center gap-2"
              >
                <Plus size={16} weight="bold" /> Add Department
              </button>
            </div>
          </div>

          {showForm && (
            <div data-testid="dept-form" className="bg-white border border-slate-200 p-6 mb-8 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold" style={{fontFamily: 'Outfit, sans-serif'}}>{editing ? "Edit Department" : "New Department"}</h3>
                <button onClick={() => { setShowForm(false); setEditing(null); }} className="p-1 hover:bg-slate-100"><X size={18} /></button>
              </div>
              <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className={labelCls}>Name *</label><input data-testid="dept-name" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className={inputCls} /></div>
                <div><label className={labelCls}>Code</label><input data-testid="dept-code" value={form.code} onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})} className={inputCls} placeholder="MKTG" /></div>
                <div>
                  <label className={labelCls}>Parent Department</label>
                  <select data-testid="dept-parent" value={form.parent_department_id} onChange={(e) => setForm({...form, parent_department_id: e.target.value})} className={inputCls}>
                    <option value="">None</option>
                    {items.filter(d => d.department_id !== editing?.department_id).map(d => <option key={d.department_id} value={d.department_id}>{d.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Department Head</label>
                  <select data-testid="dept-head" value={form.department_head_id} onChange={(e) => {
                    const u = users.find(x => x.user_id === e.target.value);
                    setForm({...form, department_head_id: e.target.value, department_head_name: u?.name || ""});
                  }} className={inputCls}>
                    <option value="">— Not assigned —</option>
                    {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name} ({u.role})</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Status</label>
                  <select data-testid="dept-status" value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className={inputCls}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="md:col-span-2"><label className={labelCls}>Description</label><textarea data-testid="dept-desc" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className={inputCls} rows="2" /></div>
                <div className="md:col-span-2"><button data-testid="dept-save-btn" type="submit" className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-2.5 font-medium">Save</button></div>
              </form>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20"><div className="loading-spinner"></div></div>
          ) : (
            <div data-testid="dept-table" className="bg-white border border-slate-200 shadow-sm overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Department</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Code</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Head</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Description</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Status</th>
                    <th className="text-right p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((d, i) => (
                    <tr key={d.department_id} data-testid={`dept-row-${i}`} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Buildings size={14} className="text-[#002FA7]" />
                          <span className="font-semibold">{d.name}</span>
                        </div>
                      </td>
                      <td className="p-3 text-xs uppercase tracking-wider text-slate-600 font-mono">{d.code || "—"}</td>
                      <td className="p-3 text-sm text-slate-700">{d.department_head_name || <span className="text-slate-400">Not assigned</span>}</td>
                      <td className="p-3 text-sm text-slate-600 max-w-md truncate">{d.description || "—"}</td>
                      <td className="p-3"><span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border ${d.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>{d.status}</span></td>
                      <td className="p-3 text-right">
                        <button data-testid={`dept-edit-${i}`} onClick={() => startEdit(d)} className="p-1.5 text-slate-700 hover:bg-slate-100"><PencilSimple size={14} /></button>
                        <button data-testid={`dept-delete-${i}`} onClick={() => remove(d)} className="p-1.5 text-rose-700 hover:bg-rose-50 ml-1"><Trash size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDepartments;
